import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("hd_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const impersonate = async (userId) => {
    const { data } = await api.post("/auth/impersonate", { user_id: userId });
    // Save admin's original token so we can restore on stop (for the Authorization header fallback)
    const currentToken = localStorage.getItem("hd_token");
    if (currentToken) localStorage.setItem("hd_admin_token", currentToken);
    if (data.token) localStorage.setItem("hd_token", data.token);
    await fetchMe();
    return data.user;
  };

  const stopImpersonation = async () => {
    try { await api.post("/auth/impersonate/stop"); } catch { /* ignore */ }
    const backup = localStorage.getItem("hd_admin_token");
    if (backup) {
      localStorage.setItem("hd_token", backup);
      localStorage.removeItem("hd_admin_token");
    } else {
      localStorage.removeItem("hd_token");
    }
    await fetchMe();
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("hd_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("hd_token");
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: fetchMe, impersonate, stopImpersonation, formatApiError }),
    [user, loading, fetchMe]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
